import * as React from "react";

import {
  ViewingContext,
  ViewingContextMap,
  ViewingContextSourceName,
} from "./ViewingContext";
import { ViewingContextService } from "./ViewingContextService";

export const useRegisterViewingContext = <T extends ViewingContextSourceName>(
  name: T,
  value: Partial<ViewingContextMap[T]>,
) => {
  // Generate a stable ID for this component instance
  const componentId = React.useMemo(() => Symbol(name), [name]);

  // Deep compare the value to avoid unnecessary updates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedValue = React.useMemo(() => value, [JSON.stringify(value)]);

  React.useEffect(
    function addContext() {
      const service = ViewingContextService.getInstance();
      service.addViewingContextPart(componentId, name, memoizedValue);

      return function removeContext() {
        service.removeViewingContextPart(componentId);
      };
    },
    [componentId, name, memoizedValue],
  );
};

export const useViewingContext = (): ViewingContext => {
  const service = ViewingContextService.getInstance();

  return React.useSyncExternalStore(
    service.subscribe.bind(service),
    service.getViewingContext.bind(service),
  );
};
